import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getStorage } from 'firebase/storage';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { makeStallSafeProvider } from './utils/stallSafeAppCheck';

const firebaseConfig = {
	apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
	authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
	databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
	projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
	storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
	messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
	appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

export const app = initializeApp(firebaseConfig);

// Firebase App Check (reCAPTCHA v3) — attaches an attestation token to calls so
// the public callables (submitInquiry, createCheckoutSession, createPackCheckout,
// joinWorkshopWaitlist) can't be hit directly to spam leads or abuse email.
// The site key comes from REACT_APP_RECAPTCHA_SITE_KEY (never hardcoded).
//
// IMPORTANT: only initialize in PRODUCTION builds by default. On localhost,
// reCAPTCHA can't attest a dev machine, and attaching an unregistered/invalid
// App Check token would break ordinary Firebase reads/calls the moment any
// console-side enforcement is on — so local dev stays App-Check-free and behaves
// exactly as before. To exercise App Check locally, set REACT_APP_APPCHECK_LOCAL
// =true: that enables the debug-token path (the SDK prints a token to register
// once in the Firebase console — see docs/LOCAL-VERIFY.md). Init is wrapped in
// try/catch so App Check can never block app boot. Missing site key => no-op.
const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const appCheckLocal = process.env.REACT_APP_APPCHECK_LOCAL === 'true';
const wantAppCheck = process.env.NODE_ENV === 'production' || appCheckLocal;
if (wantAppCheck && recaptchaSiteKey) {
	try {
		if (appCheckLocal && typeof window !== 'undefined') {
			// `true` makes the SDK MINT a fresh debug token per browser profile,
			// which then has to be registered by hand — and until it is, App
			// Check 403s poison every Firebase read, not just the callable (the
			// hazard the comment above describes). Supplying an ALREADY-
			// REGISTERED token via REACT_APP_APPCHECK_DEBUG_TOKEN skips that
			// loop entirely and works in any browser on this machine.
			// eslint-disable-next-line no-undef
			window.FIREBASE_APPCHECK_DEBUG_TOKEN =
				window.FIREBASE_APPCHECK_DEBUG_TOKEN ||
				process.env.REACT_APP_APPCHECK_DEBUG_TOKEN ||
				true;
		}
		// Stall-safe: a reCAPTCHA that never becomes ready (Safari, home-screen
		// apps, a tab suspended mid-lesson) used to hang EVERY Firestore read
		// behind one never-settling token request. See utils/stallSafeAppCheck.
		const StallSafeReCaptchaV3Provider = makeStallSafeProvider(ReCaptchaV3Provider, {
			// eslint-disable-next-line no-console
			onStall: () => console.warn('App Check: reCAPTCHA did not respond; continuing without a token'),
		});
		initializeAppCheck(app, {
			provider: new StallSafeReCaptchaV3Provider(recaptchaSiteKey),
			isTokenAutoRefreshEnabled: true,
		});
	} catch (err) {
		// Never let App Check setup break app boot.
		// eslint-disable-next-line no-console
		console.error('App Check init failed (continuing without it):', err);
	}
} else if (!recaptchaSiteKey) {
	// eslint-disable-next-line no-console
	console.warn('⚠️ App Check disabled: REACT_APP_RECAPTCHA_SITE_KEY not set.');
}

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

// Full local stack against the Firebase Emulator Suite — opt-in via
// REACT_APP_USE_EMULATORS=true (see docs/LOCAL-VERIFY.md). When this flag is
// OFF (the default), the app talks to the real Firebase project, preserving
// the prior behavior of connecting only the Functions emulator on localhost.
if (process.env.REACT_APP_USE_EMULATORS === 'true') {
	// Use the host the page was loaded from, so the emulator works on localhost
	// AND from another device on the LAN (e.g. a phone at http://<mac-ip>:3000 →
	// emulator calls go back to the Mac, not the phone's own localhost).
	const emHost =
		typeof window !== 'undefined' && window.location.hostname
			? window.location.hostname
			: 'localhost';
	connectFirestoreEmulator(db, emHost, 8080);
	connectFunctionsEmulator(functions, emHost, 5001);
	connectAuthEmulator(auth, `http://${emHost}:9099`, { disableWarnings: true });
	// eslint-disable-next-line no-console
	console.info(`🔧 Firebase Emulators ON (${emHost}) — Firestore:8080, Functions:5001, Auth:9099`);
} else if (
	window.location.hostname === 'localhost' &&
	process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === 'true'
) {
	// Functions-only emulator on localhost — now OPT-IN.
	//
	// Previously this connected automatically on localhost while Firestore/Auth
	// stayed on prod. That mixed mode silently broke every CALLABLE (e.g. the
	// Inquiries "Reviewed" write -> setInquiryStatus) whenever the Functions
	// emulator wasn't running, even though direct Firestore writes still worked
	// against prod — a confusing asymmetry that made callable writes look broken
	// during local UI verification. Default localhost now talks to PROD
	// functions, consistent with Firestore/Auth; set REACT_APP_USE_FUNCTIONS_
	// EMULATOR=true (or the full REACT_APP_USE_EMULATORS=true stack) to develop
	// against local functions.
	try {
		connectFunctionsEmulator(functions, 'localhost', 5001);
		// eslint-disable-next-line no-console
		console.info('🔧 Functions emulator ON (localhost:5001) — opt-in.');
	} catch (error) {
		// Emulator may already be connected during hot reload.
	}
}
